import { useParams } from "react-router-dom";
import { PILLARS, getPillar } from "../../data/ybf/pillars";
import { PILLAR_KPIS } from "../../data/ybf/kpis";
import { grantsForPillar, ragForScore } from "../../data/ybf/grants";
import { STORIES } from "../../data/ybf/stories";
import { TARGETS } from "../../data/ybf/targets";
import KPIScorecard from "../../components/ybf/KPIScorecard";
import TargetProgress from "../../components/ybf/TargetProgress";
import StoryCard from "../../components/ybf/StoryCard";
import { GraduationCap, Briefcase, User, TrendingUp, Users, MapPin, Sprout, TreePine, Leaf, Factory, Zap, Atom, Laptop, Cpu, BookOpen, Recycle, Wifi, CheckCircle2, XCircle, Target as TargetIcon } from "lucide-react";
import { fmtCr, fmtInt } from "../../data/ybf/utils";

const ICONS = { GraduationCap, Briefcase, User, TrendingUp, Users, MapPin, Sprout, TreePine, Leaf, Factory, Zap, Atom, Laptop, Cpu, BookOpen, Recycle, Wifi };

export default function PillarPage({ pillarId: forcedId }) {
  const params = useParams();
  const pillarId = forcedId || params.pillarId;
  const p = getPillar(pillarId);
  if (!p) return <div className="p-6 text-slate-600">Pillar not found.</div>;

  const kpis     = PILLAR_KPIS[pillarId] || [];
  const grants   = grantsForPillar(pillarId);
  const stories  = STORIES.filter((s) => s.pillar === pillarId);
  const targets  = TARGETS.filter((t) => t.pillar === pillarId);

  const activeCount  = grants.filter((g) => g.status === "Active").length;
  const closedCount  = grants.filter((g) => g.status === "Closed").length;
  const avgScore     = grants.length
    ? (grants.reduce((s, g) => s + g.score, 0) / grants.length).toFixed(2)
    : "—";

  return (
    <div data-testid={`pillar-page-${pillarId}`} className="space-y-5 pb-8">
      {/* Pillar hero */}
      <div
        className="rounded-2xl p-6 text-white relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${p.color}, ${p.accent})` }}
      >
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/10 blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">{p.short} · Lenovo Focus Area</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">{p.name}</h1>
          <p className="text-sm text-white/85 mt-2 max-w-2xl leading-relaxed">{p.description}</p>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {p.subThemes.map((t) => (
              <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/15 text-white font-medium backdrop-blur">
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Pillar KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => {
          const Icon = ICONS[k.icon] || TrendingUp;
          return (
            <KPIScorecard
              key={k.key}
              icon={Icon}
              label={k.label}
              value={k.value}
              unit={k.unit}
              accent={p.color}
            />
          );
        })}
      </div>

      {/* KPIs (full width grid) */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TargetIcon size={16} className="text-[#E2231A]" />
          <h3 className="text-sm font-bold text-slate-900">KPIs</h3>
          <span className="text-[11px] text-slate-500">
            {targets.length} measurable commitment{targets.length === 1 ? "" : "s"} for this pillar
          </span>
        </div>
        {targets.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {targets.map((t) => <TargetProgress key={t.id} target={t} />)}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 text-xs text-slate-500">
            No public KPIs disclosed for this pillar.
          </div>
        )}
      </div>

      {/* Grants Overview */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Projects Overview</h3>
            <p className="text-[11px] text-slate-500">
              {grants.length} projects delivering this pillar · avg score{" "}
              <span className="font-semibold text-slate-700">{avgScore}/5</span>
            </p>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200">
              <CheckCircle2 size={11} /> {activeCount} Active
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold border border-slate-200">
              <XCircle size={11} /> {closedCount} Closed
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid={`pillar-grants-table-${pillarId}`}>
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left  font-medium text-slate-600 uppercase py-2 px-3 whitespace-nowrap">Project ID</th>
                <th className="text-left  font-medium text-slate-600 uppercase py-2 px-3">Project Name</th>
                <th className="text-left  font-medium text-slate-600 uppercase py-2 px-3">Partner</th>
                <th className="text-left  font-medium text-slate-600 uppercase py-2 px-3">States</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3 whitespace-nowrap">Beneficiaries</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3">Sanctioned</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3">Disbursed</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3">Utilised</th>
                <th className="text-right font-medium text-slate-600 uppercase py-2 px-3">Since</th>
                <th className="text-center font-medium text-slate-600 uppercase py-2 px-3">Status</th>
                <th className="text-center font-medium text-slate-600 uppercase py-2 px-3">Score</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => {
                const rag = ragForScore(g.score);
                const isActive = g.status === "Active";
                return (
                  <tr key={g.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`grant-row-${g.id}`}>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <span className="font-mono text-[10px] font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                        {g.id}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-semibold text-slate-900">{g.name}</td>
                    <td className="py-2 px-3 text-slate-700">{g.partnerName}</td>
                    <td className="py-2 px-3 text-slate-600 max-w-[160px] truncate" title={g.states.join(", ")}>
                      {g.states.join(", ")}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-slate-800">
                      {g.beneficiaries ? fmtInt(g.beneficiaries) : (g.trees ? `${fmtInt(g.trees)} trees` : "—")}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-slate-800">{fmtCr(g.fundedCr)}</td>
                    <td className="py-2 px-3 text-right" data-testid={`grant-disbursed-${g.id}`}>
                      <div className="font-medium text-slate-800">{fmtCr(g.disbursedCr)}</div>
                      <DisburseChip pct={g.disbursePct} />
                    </td>
                    <td className="py-2 px-3 text-right" data-testid={`grant-utilised-${g.id}`}>
                      <div className="font-medium text-slate-800">{fmtCr(g.utilisedCr)}</div>
                      <UtiliseChip pct={g.utilisePct} />
                    </td>
                    <td className="py-2 px-3 text-right text-slate-500">{g.since}</td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                          isActive
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-slate-100 text-slate-600 border-slate-200"
                        }`}
                        data-testid={`grant-status-${g.id}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-400"}`} />
                        {g.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${rag.bg} ${rag.text} ${rag.border}`}
                        data-testid={`grant-score-${g.id}`}
                        title={`${rag.label} · ${g.score.toFixed(2)}/5`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: rag.dot }} />
                        {g.score.toFixed(1)}/5
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stories */}
      {stories.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-900 mb-3">Stories from the field</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stories.map((s) => <StoryCard key={s.id} story={s} variant="compact" />)}
          </div>
        </div>
      )}
    </div>
  );
}

function pctTone(pct, thresholds) {
  // thresholds = [greenMin, amberMin] e.g. [80, 60]
  if (pct >= thresholds[0]) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (pct >= thresholds[1]) return "bg-amber-50 text-amber-700 border-amber-200";
  return                        "bg-rose-50 text-rose-700 border-rose-200";
}

function DisburseChip({ pct }) {
  return (
    <span className={`inline-block mt-0.5 px-1.5 py-0 rounded text-[9px] font-bold border ${pctTone(pct, [85, 65])}`}>
      {pct}% disb.
    </span>
  );
}

function UtiliseChip({ pct }) {
  return (
    <span className={`inline-block mt-0.5 px-1.5 py-0 rounded text-[9px] font-bold border ${pctTone(pct, [85, 70])}`}>
      {pct}% util.
    </span>
  );
}

